import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth.jsx'
import { isCognitoConfigured, signUp, confirmSignUp, displayName } from '../lib/cognito.js'
import AuthShell, { AuthField, AuthPasswordField, AuthButton, AuthError } from '../components/AuthShell.jsx'

export default function Signup() {
  const { login, setSession, isCognito } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState('form') // 'form' | 'confirm'
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function handleSignup(e) {
    e.preventDefault()
    setError('')
    if (!name.trim() || !email.trim() || password.length < 8) {
      setError('Enter your name, email, and a password of at least 8 characters.')
      return
    }
    setBusy(true)
    try {
      if (isCognitoConfigured) {
        await signUp({ email, password, name })
        setStep('confirm')
      } else {
        // Demo fallback — no backend to verify against. Split the name the same
        // way signUp() splits it for Cognito, otherwise the session carries a
        // display name with no first/last behind it and Settings shows someone
        // who *just typed their name* two empty fields.
        const [given, ...rest] = name.trim().split(/\s+/)
        const firstName = given || ''
        const lastName = rest.join(' ')
        setSession({
          email: email.trim(),
          firstName,
          lastName,
          name: displayName({ email: email.trim(), firstName, lastName }),
          role: 'Member',
        })
        navigate('/', { replace: true })
      }
    } catch (err) {
      setError(err.message || 'Sign up failed.')
    } finally {
      setBusy(false)
    }
  }

  async function handleConfirm(e) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      await confirmSignUp(email, code)
      await login(email.trim(), password)
      navigate('/', { replace: true })
    } catch (err) {
      setError(err.message || 'Confirmation failed.')
    } finally {
      setBusy(false)
    }
  }

  if (step === 'confirm') {
    return (
      <AuthShell title="Verify your email" subline={<>we sent a code to {email}</>}>
        <form onSubmit={handleConfirm} className="flex flex-col gap-y-3">
          <AuthField
            label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            autoComplete="one-time-code"
            autoFocus
          />
          <AuthError>{error}</AuthError>
          <AuthButton disabled={busy}>{busy ? 'Verifying…' : 'Enter'}</AuthButton>
        </form>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Create your account"
      subline={
        <>
          or <Link to="/login">sign in</Link>
        </>
      }
      below={
        !isCognito && (
          <div className="text-[12px] text-[#5c5c5c]">
            Demo mode — sign-up just starts a local session.
          </div>
        )
      }
    >
      <form onSubmit={handleSignup} className="flex flex-col gap-y-3">
        <AuthField
          label="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Doe"
          autoComplete="name"
          autoFocus
        />
        <AuthField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoComplete="username"
        />
        <AuthPasswordField
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          autoComplete="new-password"
        />
        <AuthError>{error}</AuthError>
        <AuthButton disabled={busy}>{busy ? 'Creating…' : 'Enter'}</AuthButton>
      </form>
    </AuthShell>
  )
}
